import React, { useEffect, useState } from 'react';
import { BellIcon, UserIcon, HelpCircleIcon, XIcon, CheckCircleIcon, PackageIcon, LogOutIcon, UsersIcon } from 'lucide-react';
import { User, UserManagement } from './UserManagement';
import { Impressum } from './Impressum';
interface HeaderProps {
  onBackToOverview?: () => void;
  currentUser: User | null;
  onLogout: () => void;
}
interface Notification {
  id: string;
  message: string;
  timestamp: Date;
  read: boolean;
  type: 'success' | 'info' | 'warning';
}
export function Header({
  onBackToOverview,
  currentUser,
  onLogout
}: HeaderProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showImpressum, setShowImpressum] = useState(false);
  const [snackbar, setSnackbar] = useState<Notification | null>(null);
  // All modules for user management permissions
  const allModules = [{
    id: 'Auftragsplanung',
    title: 'Auftragsplanung'
  }, {
    id: 'Stammdaten',
    title: 'Stammdaten'
  }, {
    id: 'Bereitstellung',
    title: 'Bereitstellung'
  }, {
    id: 'TK Linien',
    title: 'TK Linien'
  }, {
    id: 'Verwiegung',
    title: 'Verwiegung'
  }, {
    id: 'Rückverfolgung',
    title: 'Rückverfolgung'
  }, {
    id: 'Mehl und Öl Verwiegung',
    title: 'Mehl und Öl Verwiegung'
  }, {
    id: 'Gewürzverwiegung',
    title: 'Gewürzverwiegung'
  }, {
    id: 'TK-Lager',
    title: 'TK-Lager'
  }, {
    id: 'Gewürzlager',
    title: 'Gewürzlager'
  }, {
    id: 'Mischer',
    title: 'Mischer'
  }, {
    id: 'Mehrkopfwaage',
    title: 'Mehrkopfwaage'
  }, {
    id: 'Kochanlage',
    title: 'Kochanlage'
  }];
  // Notifications disabled - removed the useEffect that generates notifications
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showNotifications || showUserMenu) {
        const target = event.target as HTMLElement;
        if (!target.closest('.notification-dropdown') && !target.closest('.user-dropdown')) {
          setShowNotifications(false);
          setShowUserMenu(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications, showUserMenu]);
  const unreadCount = 0; // No notifications
  const markAllAsRead = () => {
    setNotifications([]);
  };
  const markAsRead = (id: string) => {
    // No-op since notifications are disabled
  };
  const removeNotification = (id: string) => {
    // No-op since notifications are disabled
  };
  const handleLogoClick = () => {
    setIsLoading(true);
    // Short timeout to show the loading state
    setTimeout(() => {
      // Use the onBackToOverview callback if available, otherwise fallback to location change
      if (typeof onBackToOverview === 'function') {
        onBackToOverview();
      } else {
        window.location.href = '/';
      }
    }, 500); // Short delay for visual feedback
  };
  const handleOpenUserManagement = () => {
    setShowUserMenu(false);
    setShowUserManagement(true);
  };
  return <>
      {isLoading && <div className="fixed inset-0 bg-white/70 backdrop-blur-sm z-50 flex items-center justify-center" data-id="element-464">
          <div className="w-12 h-12 border-4 border-[#8DC63F] border-t-transparent rounded-full animate-spin" data-id="element-465"></div>
        </div>}
      {/* Snackbar removed - notifications disabled */}
      <header className="bg-white border-b border-gray-200 py-4 px-6 md:px-8" data-id="element-466">
        <div className="flex justify-between items-center" data-id="element-467">
          <button onClick={handleLogoClick} className="flex items-center cursor-pointer" data-id="element-468">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/sip3wjubm1WrwbdT27XCT7/logo_%283%29.svg" alt="UNIQFOOD Logo" className="h-10" data-id="element-469" />
          </button>
          <div className="flex items-center space-x-4" data-id="element-470">
            <button onClick={() => setShowImpressum(true)} className="text-xs text-gray-500 hover:text-gray-700" data-id="element-471">
              Impressum
            </button>
            <button className="p-2 rounded-full hover:bg-white hover:bg-opacity-20 transition-colors" data-id="element-472">
              <HelpCircleIcon className="w-6 h-6 text-gray-700" data-id="element-473" />
            </button>
            {/* Notification icon removed - notifications disabled */}
            {/* User dropdown */}
            <div className="relative user-dropdown" data-id="element-474">
              <button onClick={() => {
              setShowUserMenu(!showUserMenu);
              setShowNotifications(false);
            }} className="p-2 rounded-full hover:bg-gray-100 transition-colors relative flex items-center" data-id="element-475">
                <UserIcon className="w-6 h-6 text-gray-700" data-id="element-476" />
              </button>
              {showUserMenu && <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg z-10" data-id="element-477">
                  <div className="p-3 border-b border-gray-200" data-id="element-478">
                    <div className="font-semibold text-gray-700" data-id="element-479">
                      {currentUser?.username}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-480">
                      Angemeldet als Benutzer
                    </div>
                  </div>
                  <div className="py-1" data-id="element-481">
                    {currentUser?.username === 'admin' && <button onClick={handleOpenUserManagement} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center" data-id="element-482">
                        <UsersIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-483" />
                        Benutzerverwaltung
                      </button>}
                    <button onClick={onLogout} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center" data-id="element-484">
                      <LogOutIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-485" />
                      Abmelden
                    </button>
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </header>
      {/* User Management Modal */}
      {showUserManagement && currentUser && <UserManagement onClose={() => setShowUserManagement(false)} currentUser={currentUser} allModules={allModules} data-id="element-486" />}
      {/* Impressum Modal */}
      {showImpressum && <Impressum onClose={() => setShowImpressum(false)} data-id="element-487" />}
    </>;
}